import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import { SystemStyleObject } from '@mui/system';
import React from 'react';

import { CopyableField } from '@/components/atoms/CopyableField';
import { LabeledAmount } from '@/components/molecules/LabeledAmount';
import { ALL_FIELDS, FIELDS_BY_RAIL } from '@/components/organisms/fiatAccountDetailsUtils';
import { PaymentInstructionsResponse } from '@/DTOs/paymentInstruction';
import { useToast } from '@/hooks/useToast';
import { useTranslator } from '@/hooks/useTranslator';
import { CurrencyCodeType, PaymentRailType } from '@/types/currency';
import { LabelAndValue } from '@/types/labelAndValue';

interface Props {
  readonly paymentInstructions: PaymentInstructionsResponse;
  readonly paymentRail?: PaymentRailType;
  readonly fromAmount?: number | null;
  readonly fromCurrency?: CurrencyCodeType;
  readonly toAmount?: number | null;
  readonly toCurrency?: CurrencyCodeType;
}

export const FiatAccountDetailsContent: React.FC<Props> = (props: Props): React.ReactElement => {
  const { paymentInstructions, paymentRail, fromAmount, fromCurrency, toAmount, toCurrency } =
    props;
  const tr = useTranslator();
  const toast = useToast();

  const fields = paymentRail ? (FIELDS_BY_RAIL[paymentRail] ?? ALL_FIELDS) : ALL_FIELDS;

  const items = React.useMemo((): readonly LabelAndValue<string | React.ReactElement>[] => {
    if (!paymentInstructions) {
      return [];
    }

    const result: LabelAndValue<string | React.ReactElement>[] = [];

    for (const field of fields) {
      switch (field) {
        case 'beneficiaryName':
          result.push({
            label: tr('BENEFICIARY_NAME'),
            value: <CopyableField text={paymentInstructions.receiverName} />,
          });
          break;
        case 'beneficiaryAddress':
          if (paymentInstructions.receiverAddress) {
            result.push({
              label: tr('BENEFICIARY_ADDRESS'),
              value: <CopyableField text={paymentInstructions.receiverAddress} />,
            });
          }
          break;
        case 'bankInfo':
          result.push({
            label: tr('BANK_INFORMATION'),
            value: (
              <Box>
                <CopyableField text={paymentInstructions.receiverBankName} />
                <CopyableField text={paymentInstructions.receiverBankAddress} />
              </Box>
            ),
          });
          break;
        case 'accountNumber':
          result.push({
            label: tr('ACCOUNT_NUMBER', CurrencyCodeType.USD),
            value: <CopyableField text={paymentInstructions.accountNumber} />,
          });
          break;
        case 'routingNumber':
          result.push({
            label: tr('ROUTING_NUMBER', CurrencyCodeType.USD),
            value: <CopyableField text={paymentInstructions.routingNumber} />,
          });
          break;
        case 'swiftCode':
          if (paymentInstructions.swiftCode) {
            result.push({
              label: tr('SWIFT_CODE'),
              value: <CopyableField text={paymentInstructions.swiftCode} />,
            });
          }
          break;
        case 'reference':
          if (paymentInstructions.reference) {
            result.push({
              label: tr('REFERENCE'),
              value: <CopyableField text={paymentInstructions.reference} />,
            });
          }
          break;
      }
    }

    return result;
  }, [paymentInstructions, fields, tr]);

  React.useEffect((): VoidFunction => {
    return (): void => {
      toast.remove();
    };
  }, [toast]);

  const showAmounts =
    fromAmount != null && fromCurrency != null && toAmount != null && toCurrency != null;

  return (
    <>
      <Box sx={styles.grid}>
        {items.map((item: LabelAndValue<string | React.ReactElement>): React.ReactElement => {
          return (
            <Box key={item.label}>
              <Typography sx={styles.sectionTitle} variant="body1" color="custom.LightGray">
                {item.label}
              </Typography>
              {typeof item.value === 'string' ? (
                <Typography variant="body2">{item.value}</Typography>
              ) : (
                item.value
              )}
            </Box>
          );
        })}
      </Box>
      {showAmounts && (
        <Box sx={styles.amountSection}>
          <Divider sx={styles.divider} />
          <LabeledAmount label="YOU_SEND" amount={fromAmount} currency={fromCurrency} />
          <LabeledAmount label="YOU_RECEIVE" amount={toAmount} currency={toCurrency} />
        </Box>
      )}
    </>
  );
};

const styles: Record<string, SystemStyleObject> = {
  grid: {
    display: 'flex',
    flexDirection: 'column',
    gap: 3,
    wordBreak: 'break-word',
  },
  sectionTitle: {
    mb: 0.5,
  },
  amountSection: {
    mt: 3,
  },
  divider: {
    mb: 1,
  },
};
